import React, { useCallback } from 'react';
import {
  IncidentMedicalAidStation,
  useIncidentMedicalAidStations,
  useCreateIncidentMedicalAidStation,
  useEditIncidentMedicalAidStation,
} from 'components/IAP/hooks/incidentMedicalAidStationsHooks';
import { MedicalAidStationBase } from 'types/iapBaseTypes';
import { MedicalPlanTable, FieldDescriptor } from './MedicalPlanTable';

const FIELDS: FieldDescriptor[] = [
  {
    type: 'text',
    accessorKey: 'name',
    header: 'Name',
    placeholder: 'Name *',
    sortable: true,
    maxLength: 200,
  },
  {
    type: 'text',
    accessorKey: 'location',
    header: 'Location',
    placeholder: 'Location',
  },
  {
    type: 'text',
    accessorKey: 'contact_number',
    header: 'Contact',
    placeholder: 'Contact',
    maxLength: 40,
  },
  {
    type: 'boolean',
    accessorKey: 'paramedics_on_site',
    header: 'Paramedics On-Site',
    ariaLabel: 'Paramedics on site',
  },
];

const EMPTY_DRAFT = {
  name: '',
  location: '',
  contact_number: '',
  paramedics_on_site: false,
};

const toEditPayload = (
  medicalAidStation: IncidentMedicalAidStation,
  override: Record<string, string | boolean>
): MedicalAidStationBase & {
  id: string;
  medical_aid_station_id: string;
  archived: boolean;
} => ({
  id: medicalAidStation.id,
  medical_aid_station_id: medicalAidStation.medical_aid_station_id,
  name: medicalAidStation.name,
  description: medicalAidStation.description,
  location: medicalAidStation.location,
  contact_number: medicalAidStation.contact_number,
  paramedics_on_site: medicalAidStation.paramedics_on_site,
  archived: medicalAidStation.archived,
  ...override,
});

type Props = { readOnly?: boolean };

export const MedicalAidStationsTable: React.FC<Props> = ({ readOnly = false }) => {
  const { data: medicalAidStations } =
    useIncidentMedicalAidStations('incident');
  const activeMedicalAidStations = (medicalAidStations ?? []).filter(
    (e) => !e.archived
  );

  const { mutate: createRecord, isPending: isCreating } =
    useCreateIncidentMedicalAidStation();
  const { mutate: editRecord } = useEditIncidentMedicalAidStation();

  const onSaveDraft = useCallback(
    (draft: Record<string, string | boolean>, onSuccess: () => void) => {
      createRecord(
        {
          name: draft.name as string,
          location: draft.location as string,
          contact_number: draft.contact_number as string,
          paramedics_on_site: draft.paramedics_on_site as boolean,
        },
        { onSuccess }
      );
    },
    [createRecord]
  );

  const onSaveField = useCallback(
    (
      entity: IncidentMedicalAidStation,
      field: string,
      value: string | boolean
    ) => {
      editRecord(toEditPayload(entity, { [field]: value }));
    },
    [editRecord]
  );

  const onDelete = useCallback(
    (entity: IncidentMedicalAidStation) => {
      editRecord(toEditPayload(entity, { archived: true }));
    },
    [editRecord]
  );

  return (
    <MedicalPlanTable
      title="Medical Aid Stations"
      addButtonLabel="Add Station"
      noDataMessage="No medical aid stations defined. Click Add Station to get started."
      entities={activeMedicalAidStations}
      emptyDraft={EMPTY_DRAFT}
      requiredField="name"
      fields={FIELDS}
      isCreating={isCreating}
      onSaveDraft={onSaveDraft}
      onSaveField={onSaveField}
      onDelete={onDelete}
      readOnly={readOnly}
    />
  );
};
